import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  twoFactorService,
  ConfirmEnable2faDto,
} from "@/services/twoFactorService";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export const twoFactorKeys = {
  all: ["twoFactor"] as const,
  status: () => [...twoFactorKeys.all, "status"] as const,
  accountUrl: () => [...twoFactorKeys.all, "accountUrl"] as const,
} as const;

export const useTwoFactorStatus = (enabled: boolean = true) => {
  return useQuery({
    queryKey: twoFactorKeys.status(),
    queryFn: () => twoFactorService.getStatus(),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
};

export const useRequestEnable2fa = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: () => twoFactorService.requestEnable(),
    onError: (error: Error) => {
      const message = error.message;
      if (message?.includes("No phone number")) {
        toast.error(t("account.security.twoFactor.noPhone"));
      } else if (message?.includes("already enabled")) {
        toast.error(t("account.security.twoFactor.alreadyEnabled"));
      } else {
        toast.error(message || t("account.security.twoFactor.rateLimitSend"));
      }
    },
  });
};

export const useConfirmEnable2fa = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: ConfirmEnable2faDto) =>
      twoFactorService.confirmEnable(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: twoFactorKeys.status() });
      toast.success(t("account.security.twoFactor.enableSuccess"));
    },
    onError: (error: Error) => {
      const message = error.message;
      if (message?.includes("Invalid OTP")) {
        toast.error(t("account.security.twoFactor.invalidCode"));
      } else {
        toast.error(message || t("account.security.twoFactor.rateLimitVerify"));
      }
    },
  });
};

export const useDisable2fa = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: () => twoFactorService.disable(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: twoFactorKeys.status() });
      toast.success(t("account.security.twoFactor.disableSuccess"));
    },
  });
};

export const useAccountUrl = (enabled: boolean = true) => {
  return useQuery({
    queryKey: twoFactorKeys.accountUrl(),
    queryFn: () => twoFactorService.getAccountUrl(),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
};
